
/**
 * Unified Cache Aggregator
 * Aggregates existing cache services without replacing them
 * Provides a unified view while reusing all existing cache logic
 */

import { quotesCacheService } from './quotes-cache-service';
import { greeksCacheService } from './greeks-cache-service.client';
import { createLogger } from '@/utils/logger';

const logger = createLogger('unified-cache-aggregator');

type PositionSnapshot = Record<string, unknown>;

interface PositionCacheEntry {
  data: PositionSnapshot;
  cachedAt: number;
  accountNumber: string;
  symbol: string;
}

interface StoredPositionCache {
  positions?: Record<string, PositionCacheEntry>;
  savedAt: number;
}

function isPositionCacheEntry(value: unknown): value is PositionCacheEntry {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const entry = value as Partial<PositionCacheEntry>;
  return (
    typeof entry.accountNumber === 'string' &&
    typeof entry.symbol === 'string' &&
    typeof entry.cachedAt === 'number' &&
    entry.data !== undefined
  );
}

class UnifiedCacheAggregator {
  private positionsCache = new Map<string, PositionCacheEntry>();
  private readonly POSITION_CACHE_KEY = 'tastymaestro_positions_cache';

  constructor() {
    // Load cached positions on initialization
    this.loadPositionsFromStorage();

    // Save positions periodically
    if (typeof window !== 'undefined') {
      setInterval(() => this.savePositionsToStorage(), 30000); // Every 30s
      window.addEventListener('beforeunload', () => this.savePositionsToStorage());
    }
  }

  // Position caching (new functionality)
  updatePosition(accountId: string, symbol: string, data: object | null | undefined): void {
    if (!data) {
      // Remove position if data is null
      this.positionsCache.delete(`${accountId}_${symbol}`);
      return;
    }

    const key = `${accountId}_${symbol}`;
    const snapshot: PositionSnapshot = { ...(data as Record<string, unknown>) };
    this.positionsCache.set(key, {
      data: snapshot,
      cachedAt: Date.now(),
      accountNumber: accountId,
      symbol,
    });

    logger.debug(`[Cache] Updated position ${symbol} for account ${accountId}`);
  }

  getPosition(accountId: string, symbol: string): PositionSnapshot | null {
    const entry = this.positionsCache.get(`${accountId}_${symbol}`);
    return entry ? entry.data : null;
  }

  getAllPositions(): Map<string, PositionSnapshot> {
    const result = new Map<string, PositionSnapshot>();
    this.positionsCache.forEach((entry, key) => {
      result.set(key, entry.data);
    });
    return result;
  }

  // Delegate quotes to existing service
  getQuote(symbol: string) {
    return quotesCacheService.getQuote(symbol);
  }

  getAllQuotes() {
    return quotesCacheService.getAllQuotes();
  }

  // Delegate Greeks to existing service
  getGreeks(symbol: string) {
    return greeksCacheService.getGreeks(symbol);
  }

  getAllGreeks() {
    return greeksCacheService.getAllGreeks();
  }

  // Unified view combining all caches
  getAllData() {
    const quotes = this.getAllQuotes();
    const greeks = this.getAllGreeks();

    return {
      positions: this.getAllPositions(),
      quotes,
      greeks,
      metadata: {
        positionCount: this.positionsCache.size,
        quotesCount: quotes.size,
        greeksCount: greeks.size,
        timestamp: Date.now(),
      },
    };
  }

  // Clear stale position data
  clearStalePositions(maxAgeMs: number = 300000): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.positionsCache.forEach((entry, key) => {
      if (now - entry.cachedAt > maxAgeMs) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.positionsCache.delete(key));

    if (keysToDelete.length > 0) {
      logger.debug(`[Cache] Cleared ${keysToDelete.length} stale positions`);
    }
  }

  // Position persistence
  private savePositionsToStorage(): void {
    try {
      if (typeof window === 'undefined') return; // SSR: no localStorage
      const positions = Object.fromEntries(this.positionsCache) as Record<string, PositionCacheEntry>;
      const payload: StoredPositionCache = {
        positions,
        savedAt: Date.now(),
      };
      localStorage.setItem(this.POSITION_CACHE_KEY, JSON.stringify(payload));
    } catch (error) {
      logger.error('Failed to save positions cache:', error);
    }
  }

  private loadPositionsFromStorage(): void {
    try {
      if (typeof window === 'undefined') return; // SSR: no localStorage
      const stored = localStorage.getItem(this.POSITION_CACHE_KEY);
      if (!stored) return;

      const payload = JSON.parse(stored) as StoredPositionCache | null;
      if (!payload?.positions || typeof payload.savedAt !== 'number') {
        return;
      }

      const now = Date.now();
      const MAX_AGE = 3600000; // 1 hour

      if (now - payload.savedAt >= MAX_AGE) {
        return;
      }

      const restored = new Map<string, PositionCacheEntry>();
      Object.entries(payload.positions).forEach(([key, entry]) => {
        if (isPositionCacheEntry(entry)) {
          restored.set(key, entry);
        }
      });

      this.positionsCache = restored;
      if (restored.size > 0) {
        logger.debug(`[Cache] Loaded ${restored.size} cached positions`);
      }
    } catch (error) {
      logger.error('Failed to load positions cache:', error);
    }
  }
}

// Export singleton instance
export const cacheAggregator = new UnifiedCacheAggregator();
